<template>
  <section class="settings-parcelas">
    <!-- Header Section -->
    <b-row>
      <b-col cols="12">
        <b-card
          class="settings-parcelas__header mb-4 border-0 shadow-sm"
        >
          <div class="d-flex justify-content-between align-items-start flex-wrap">
            <div class="mr-2">
              <h4 class="mb-1 text-primary d-flex align-items-center">
                <feather-icon
                  icon="CreditCardIcon"
                  size="24"
                  class="mr-2"
                />
                Configuração de Parcelas
              </h4>
              <p class="text-muted mb-0">
                Defina as regras e valores mínimos para o parcelamento de compras
              </p>
              <small
                v-if="lastUpdatedText"
                class="text-muted d-block mt-1"
              >
                Última atualização: <span class="font-weight-bolder">{{ lastUpdatedText }}</span>
              </small>
            </div>

            <div class="d-flex align-items-center mt-2 mt-sm-0">
              <b-button
                variant="outline-primary"
                size="sm"
                class="settings-parcelas__refresh"
                :disabled="loading"
                @click="fetchParcelas"
              >
                <feather-icon
                  icon="RefreshCwIcon"
                  size="16"
                  class="mr-50"
                />
                {{ loading ? 'Atualizando...' : 'Atualizar' }}
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Parcelas List -->
    <b-row>
      <b-col cols="12">
        <b-card
          no-body
          class="shadow-sm border-0"
        >
          <b-card-header
            class="settings-parcelas__list-header d-flex justify-content-between align-items-center"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                icon="ListIcon"
                size="18"
                class="text-primary mr-1"
              />
              <div>
                <h5 class="mb-0">
                  Regras cadastradas
                </h5>
                <small class="text-muted">Clique em editar para ajustar o valor mínimo</small>
              </div>
            </div>

            <b-badge
              v-if="!loading"
              variant="light-primary"
              pill
              class="settings-parcelas__count"
            >
              {{ parcelas.length }} / 12
            </b-badge>
          </b-card-header>

          <b-card-body class="p-0">
            <!-- Loading State -->
            <div
              v-if="loading"
              class="settings-parcelas__state text-center"
            >
              <b-spinner variant="primary" />
              <p class="text-muted mt-2 mb-0">
                Carregando configurações...
              </p>
            </div>

            <!-- Empty State -->
            <div
              v-else-if="parcelas.length === 0"
              class="settings-parcelas__state text-center"
            >
              <div class="avatar avatar-xl bg-light-secondary mb-3 mx-auto">
                <div class="avatar-content">
                  <feather-icon
                    icon="InboxIcon"
                    size="40"
                  />
                </div>
              </div>
              <h5 class="mb-1 text-muted">
                Nenhuma configuração encontrada
              </h5>
              <p class="text-muted small mb-0">
                Entre em contato com o suporte para inicializar as parcelas.
              </p>
            </div>

            <!-- Parcelas Table -->
            <div
              v-else
              class="table-responsive settings-parcelas__table"
            >
              <table class="table table-hover table-striped mb-0">
                <thead>
                  <tr>
                    <th
                      class="text-center"
                      style="width: 140px;"
                    >
                      Nº Parcelas
                    </th>
                    <th>Valor Mínimo da Compra</th>
                    <th
                      class="text-right pr-2"
                      style="width: 140px;"
                    >
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="parcela in parcelas"
                    :key="parcela.id"
                  >
                    <td class="text-center align-middle">
                      <b-badge
                        variant="light-primary"
                        pill
                        class="settings-parcelas__badge"
                      >
                        {{ parcela.parcela }}x
                      </b-badge>
                    </td>
                    <td class="align-middle">
                      <div class="d-flex align-items-center">
                        <b-avatar
                          variant="light-success"
                          size="sm"
                          class="mr-2"
                        >
                          <feather-icon
                            icon="DollarSignIcon"
                            size="16"
                          />
                        </b-avatar>
                        <div>
                          <div class="settings-parcelas__money">
                            {{ formatCurrency(parcela.valor_minimo_compra) }}
                          </div>
                          <small class="text-muted">Valor mínimo exigido</small>
                        </div>
                      </div>
                    </td>
                    <td class="text-right align-middle pr-2">
                      <b-button
                        v-b-tooltip.hover
                        variant="flat-primary"
                        size="sm"
                        title="Editar Configuração"
                        class="btn-icon rounded-circle"
                        @click="openEditModal(parcela)"
                      >
                        <feather-icon
                          icon="Edit2Icon"
                          size="18"
                        />
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Edit Modal -->
    <b-modal
      v-model="showEditModal"
      centered
      hide-footer
      no-close-on-backdrop
      content-class="settings-parcelas__modal"
      @hidden="resetEditForm"
    >
      <template #modal-header="{ close }">
        <div class="d-flex align-items-center justify-content-between w-100">
          <div class="d-flex align-items-center">
            <div
              class="d-inline-flex align-items-center justify-content-center bg-light-primary rounded-circle mr-3"
              style="width: 48px; height: 48px;"
            >
              <feather-icon
                icon="CreditCardIcon"
                size="24"
                class="text-primary"
              />
            </div>
            <div>
              <h5 class="mb-0 font-weight-bolder text-dark">
                Editar Parcela
              </h5>
              <small class="text-muted">
                {{ editingParcela.parcela }}x - Valor mínimo de compra
              </small>
            </div>
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            class="btn-icon rounded-circle"
            @click="close()"
          >
            <feather-icon
              icon="XIcon"
              size="20"
            />
          </b-button>
        </div>
      </template>

      <b-form
        class="settings-parcelas__modal-body"
        @submit.prevent="saveParcela"
      >
        <!-- Parcela Badge Section -->
        <div class="settings-parcelas__badge-section mb-4 text-center">
          <div
            class="d-inline-flex align-items-center justify-content-center bg-light-primary rounded-circle mb-3"
            style="width: 100px; height: 100px;"
          >
            <span
              class="text-primary font-weight-bolder"
              style="font-size: 3rem;"
            >
              {{ editingParcela.parcela }}<span style="font-size: 1.5rem;">x</span>
            </span>
          </div>
          <p class="text-muted mb-0">
            Configurando regra para <strong>{{ editingParcela.parcela }} parcelas</strong>
          </p>
        </div>

        <!-- Range Information Card -->
        <div
          v-if="valorRangeInfo"
          class="settings-parcelas__range-card mb-4"
        >
          <div class="d-flex align-items-center mb-2">
            <div
              class="d-inline-flex align-items-center justify-content-center bg-light-primary rounded mr-2"
              style="width: 32px; height: 32px;"
            >
              <feather-icon
                icon="AlertCircleIcon"
                size="16"
                class="text-primary"
              />
            </div>
            <h6 class="mb-0 font-weight-bold text-dark">
              Intervalo Permitido
            </h6>
          </div>
          <p class="text-muted mb-0 pl-4">
            {{ valorRangeInfo }}
          </p>
        </div>

        <!-- Valor Mínimo Section -->
        <div class="settings-parcelas__input-section mb-4">
          <h6 class="mb-3 font-weight-bold text-dark d-flex align-items-center">
            <feather-icon
              icon="DollarSignIcon"
              size="18"
              class="text-success mr-2"
            />
            Valor Mínimo da Compra
          </h6>
          <b-form-group
            label-for="valor-minimo"
            class="mb-0"
          >
            <b-input-group class="input-group-merge">
              <b-input-group-prepend is-text>
                <strong>R$</strong>
              </b-input-group-prepend>
              <b-form-input
                id="valor-minimo"
                v-model="editingParcela.valor_minimo_compra"
                type="number"
                step="0.01"
                min="0"
                placeholder="0,00"
                :state="valorMinimoState"
                size="lg"
                class="font-weight-bold text-center"
                required
              />
            </b-input-group>
            <b-form-invalid-feedback :state="valorMinimoState">
              {{ valorMinimoErrorMessage }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end pt-3 border-top">
          <b-button
            variant="outline-secondary"
            size="md"
            class="px-4"
            :disabled="saving"
            @click="showEditModal = false"
          >
            Cancelar
          </b-button>
          <b-button
            variant="primary"
            size="md"
            type="submit"
            class="px-4 ml-2"
            :disabled="!isFormValid || saving"
          >
            <b-spinner
              v-if="saving"
              small
              class="mr-1"
            />
            {{ saving ? 'Salvando...' : 'Salvar Alterações' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </section>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BBadge,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BInputGroup,
  BInputGroupPrepend,
  BAvatar,
} from 'bootstrap-vue'
import axios from '@axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BBadge,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BInputGroup,
    BInputGroupPrepend,
    BAvatar,
  },
  data() {
    return {
      parcelas: [],
      loading: false,
      saving: false,
      showEditModal: false,
      lastUpdatedAt: null,
      editingParcela: {
        id: null,
        uuid: null,
        parcela: 1,
        valor_minimo_compra: 0,
      },
      originalParcela: null,
    }
  },
  computed: {
    lastUpdatedText() {
      if (!this.lastUpdatedAt) return null
      return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(this.lastUpdatedAt)
    },
    valorMinimoState() {
      if (this.editingParcela.valor_minimo_compra === null || this.editingParcela.valor_minimo_compra === '') {
        return null
      }
      
      const valor = parseFloat(this.editingParcela.valor_minimo_compra)
      
      // Validação básica: valor >= 0
      if (valor < 0) {
        return false
      }
      
      // Validação de ordem: não pode ser menor que a parcela anterior
      const parcelaAnterior = this.parcelas.find(p => p.parcela === this.editingParcela.parcela - 1)
      if (parcelaAnterior && valor < parseFloat(parcelaAnterior.valor_minimo_compra)) {
        return false
      }
      
      // Validação de ordem: não pode ser maior que a parcela posterior
      const parcelaPosterior = this.parcelas.find(p => p.parcela === this.editingParcela.parcela + 1)
      if (parcelaPosterior && valor > parseFloat(parcelaPosterior.valor_minimo_compra)) {
        return false
      }
      
      return true
    },
    valorMinimoErrorMessage() {
      if (this.editingParcela.valor_minimo_compra === null || this.editingParcela.valor_minimo_compra === '') {
        return 'O valor mínimo é obrigatório'
      }
      
      const valor = parseFloat(this.editingParcela.valor_minimo_compra)
      
      if (valor < 0) {
        return 'O valor mínimo deve ser maior ou igual a zero'
      }
      
      const parcelaAnterior = this.parcelas.find(p => p.parcela === this.editingParcela.parcela - 1)
      if (parcelaAnterior && valor < parseFloat(parcelaAnterior.valor_minimo_compra)) {
        return `O valor não pode ser menor que ${this.formatCurrency(parcelaAnterior.valor_minimo_compra)} (parcela ${parcelaAnterior.parcela}x)`
      }
      
      const parcelaPosterior = this.parcelas.find(p => p.parcela === this.editingParcela.parcela + 1)
      if (parcelaPosterior && valor > parseFloat(parcelaPosterior.valor_minimo_compra)) {
        return `O valor não pode ser maior que ${this.formatCurrency(parcelaPosterior.valor_minimo_compra)} (parcela ${parcelaPosterior.parcela}x)`
      }
      
      return ''
    },
    valorRangeInfo() {
      const parcelaAnterior = this.parcelas.find(p => p.parcela === this.editingParcela.parcela - 1)
      const parcelaPosterior = this.parcelas.find(p => p.parcela === this.editingParcela.parcela + 1)
      
      if (parcelaAnterior && parcelaPosterior) {
        return `Valor deve estar entre ${this.formatCurrency(parcelaAnterior.valor_minimo_compra)} e ${this.formatCurrency(parcelaPosterior.valor_minimo_compra)}`
      }

      if (parcelaAnterior) {
        return `Valor deve ser maior ou igual a ${this.formatCurrency(parcelaAnterior.valor_minimo_compra)}`
      }

      if (parcelaPosterior) {
        return `Valor deve ser menor ou igual a ${this.formatCurrency(parcelaPosterior.valor_minimo_compra)}`
      }
      
      return null
    },
    isFormValid() {
      return this.valorMinimoState === true
        && this.editingParcela.parcela >= 1
        && this.editingParcela.parcela <= 12
    },
  },
  mounted() {
    this.fetchParcelas()
  },
  methods: {
    async fetchParcelas() {
      try {
        this.loading = true
        // Buscar todas as parcelas de 1 a 12
        const promises = []
        for (let i = 1; i <= 12; i += 1) {
          promises.push(
            axios.get(`/parcelas/${i}`)
              .then(response => response.data)
              .catch(error => {
                console.error(`Erro ao buscar parcela ${i}:`, error)
                return null
              }),
          )
        }
        
        const results = await Promise.all(promises)
        this.parcelas = results
          .filter(parcela => parcela !== null)
          .sort((a, b) => a.parcela - b.parcela)

        this.lastUpdatedAt = new Date()
      } catch (error) {
        console.error('Erro ao buscar parcelas:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao Carregar',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Não foi possível carregar as configurações de parcelas.',
          },
        })
      } finally {
        this.loading = false
      }
    },
    
    openEditModal(parcela) {
      this.editingParcela = {
        id: parcela.id,
        uuid: parcela.uuid,
        parcela: parcela.parcela,
        valor_minimo_compra: parseFloat(parcela.valor_minimo_compra),
      }
      this.originalParcela = { ...this.editingParcela }
      this.showEditModal = true
    },
    
    resetEditForm() {
      this.editingParcela = {
        id: null,
        uuid: null,
        parcela: 1,
        valor_minimo_compra: 0,
      }
      this.originalParcela = null
    },
    
    async saveParcela() {
      if (!this.isFormValid) {
        return
      }

      const valor = parseFloat(this.editingParcela.valor_minimo_compra)

      // Validações adicionais
      if (this.editingParcela.parcela < 1 || this.editingParcela.parcela > 12) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro de Validação',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: 'O número de parcelas deve estar entre 1 e 12.',
          },
        })
        return
      }

      if (valor < 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro de Validação',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: 'O valor mínimo deve ser maior ou igual a zero.',
          },
        })
        return
      }

      // Validação de ordem com parcela anterior
      const parcelaAnterior = this.parcelas.find(p => p.parcela === this.editingParcela.parcela - 1)
      if (parcelaAnterior && valor < parseFloat(parcelaAnterior.valor_minimo_compra)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro de Validação',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: `O valor não pode ser menor que ${this.formatCurrency(parcelaAnterior.valor_minimo_compra)} (parcela ${parcelaAnterior.parcela}x)`,
          },
        })
        return
      }

      // Validação de ordem com parcela posterior
      const parcelaPosterior = this.parcelas.find(p => p.parcela === this.editingParcela.parcela + 1)
      if (parcelaPosterior && valor > parseFloat(parcelaPosterior.valor_minimo_compra)) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro de Validação',
            icon: 'AlertCircleIcon',
            variant: 'warning',
            text: `O valor não pode ser maior que ${this.formatCurrency(parcelaPosterior.valor_minimo_compra)} (parcela ${parcelaPosterior.parcela}x)`,
          },
        })
        return
      }

      try {
        this.saving = true
        const payload = {
          valor_minimo_compra: valor.toFixed(2),
        }

        await axios.put(`/parcelas/${this.editingParcela.id}`, payload)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Configuração de parcela atualizada com sucesso!',
          },
        })
        this.showEditModal = false
        await this.fetchParcelas()
      } catch (error) {
        console.error('Erro ao salvar parcela:', error)
        let errorMessage = 'Não foi possível salvar a configuração de parcela.'
        if (error.response && error.response.status === 400) {
          errorMessage = error.response.data.message || error.response.data.error || errorMessage
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao Salvar',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: errorMessage,
          },
        })
      } finally {
        this.saving = false
      }
    },
    
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value)
    },
  },
}
</script>

<style lang="scss" scoped>
.settings-parcelas {
  &__header {
    border-radius: 0.75rem;
  }

  &__refresh {
    white-space: nowrap;
  }

  &__list-header {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #ebe9f1;
    background: #fff;
  }

  &__count {
    font-weight: 700;
  }

  &__state {
    padding: 3.25rem 1.25rem;
  }

  &__table {
    .table {
      margin-bottom: 0;
    }

    thead th {
      position: sticky;
      top: 0;
      z-index: 2;
      background: #fff;
      font-size: 0.8rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #5e5873;
      padding: 1rem;
      border-top: 0;
      border-bottom: 1px solid #ebe9f1;
    }

    tbody td {
      padding: 1rem;
      vertical-align: middle;
      border-top: 0;
      border-bottom: 1px solid #f3f2f7;
    }

    tbody tr {
      transition: background-color 0.15s ease;

      &:hover {
        background-color: #f8f9ff;
      }
    }
  }

  &__badge {
    font-size: 0.95rem;
    padding: 0.55rem 0.85rem;
  }

  &__money {
    font-weight: 800;
    color: #2a2e34;
    line-height: 1.1;
  }
}

.table {
  th {
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 1rem;
  }

  td {
    padding: 1rem;
    vertical-align: middle;
  }

  tbody tr {
    transition: all 0.2s ease;

    &:hover {
      background-color: #f8f9ff;
    }
  }
}

::v-deep .modal-header {
  border-bottom: 2px solid #f3f2f7;
  padding: 1.75rem 1.75rem 1.5rem;
  background: #fafbfc;
}

::v-deep .modal-body {
  padding: 2rem 1.75rem;
}

.settings-parcelas__modal {
  ::v-deep .modal-header {
    border-bottom: 2px solid #f3f2f7;
    padding: 1.75rem 1.75rem 1.5rem;
    background: #fafbfc;
  }

  ::v-deep .modal-body {
    padding: 2rem 1.75rem;
  }
}

.settings-parcelas__modal-body {
  .settings-parcelas__badge-section {
    padding-bottom: 1rem;
    border-bottom: 1px solid #f3f2f7;
  }

  .settings-parcelas__range-card {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    padding: 1.25rem;

    h6 {
      font-size: 0.95rem;
      letter-spacing: 0.25px;
    }

    p {
      font-size: 0.9rem;
      line-height: 1.5;
      color: #6e7781;
    }
  }

  .settings-parcelas__input-section {
    background: #f8f9fa;
    padding: 1.25rem;
    border-radius: 0.5rem;

    h6 {
      margin-bottom: 1rem;
      font-size: 0.95rem;
      letter-spacing: 0.25px;
    }
  }

  .settings-parcelas__preview-section {
    .settings-parcelas__divider {
      height: 1px;
      background: linear-gradient(to right, transparent, #d1d5db, transparent);
    }

    ::v-deep .card {
      background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    }
  }
}
</style>
